import { z } from 'zod';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';

/**
 * Lot AY « Sous-traitance » (décisions de Maxime du 19/10/2026, maquette validée `docs/maquette/sous-traitance-1910.html`).
 *
 * - Sous-traitants = fournisseurs Axonaut (`GET /api/v2/suppliers`, lecture seule, table `suppliers`). Est
 *   sous-traitant : un fournisseur à qui on a confié au moins un devis dans le Cockpit, ou qui a une dépense en compte
 *   604 / 611 ; le classement à la main (oui / non) gagne (Réglages > Chiffres et fiche du sous-traitant).
 * - Sous-traitance d'un devis (`quote_subcontracting` + `quote_subcontracting_items`) : par ligne, montant global ou
 *   « Aucune ». Données internes du Cockpit, saisies par un membre ; rien n'est écrit dans Axonaut.
 * - Date « au plus tard » : début du projet (sinon signature) + délai des Réglages (3 mois), modifiable par devis.
 * - Dépense d'un sous-traitant rattachée d'office au plus ancien devis ouvert chez lui (calcul affiché) ; « Changer »
 *   enregistre un choix qui gagne et survit aux synchros.
 */

export const SubcontractMode = z.enum(['lines', 'global', 'none']);
export type SubcontractMode = z.infer<typeof SubcontractMode>;

/** État de la sous-traitance d'un devis : pas encore renseignée, ou un des trois modes. */
export const SubcontractStatus = z.enum(['unset', 'lines', 'global', 'none']);
export type SubcontractStatus = z.infer<typeof SubcontractStatus>;

export const SUBCONTRACT_STATUS_LABELS: Record<SubcontractStatus, string> = {
  unset: 'À renseigner',
  lines: 'Par ligne',
  global: 'Montant global',
  none: 'Aucune',
};

// ------------------------------------------------------------------ Fournisseurs

export const SubcontractorOverride = z.enum(['yes', 'no']);
/** `auto` = revenir à la règle (devis confié ou dépense 604 / 611). */
export const SubcontractorClassification = z.enum(['yes', 'no', 'auto']);
export type SubcontractorClassification = z.infer<typeof SubcontractorClassification>;

/** Un fournisseur Axonaut (copie locale), avec son classement. */
export const Supplier = z.object({
  id: Uuid,
  externalId: z.string(),
  name: z.string(),
  /** Catégories Axonaut du fournisseur (« Développement », « Graphisme »…). */
  categories: z.array(z.string()),
  /** Sous-traitant retenu (règle ou classement à la main). */
  isSubcontractor: z.boolean(),
  /** Ce que dit la règle : devis confié dans le Cockpit, dépense en 604 / 611. */
  rule: z.object({ hasQuote: z.boolean(), hasExpense: z.boolean() }),
  /** Classement à la main (null = la règle s'applique). */
  override: SubcontractorOverride.nullable(),
  /** Fiche du fournisseur dans Axonaut (sa société, `company_id`), null si inconnue. */
  axonautUrl: z.string().nullable(),
});
export type Supplier = z.infer<typeof Supplier>;

/**
 * `settings.axonautSuppliersRead` / `settings.axonautProductsRead` : dernière lecture des fournisseurs (du catalogue)
 * par la synchro Axonaut, écrite par la synchro (lecture seule dans Réglages). `error` : message en français, sans
 * secret, quand la lecture a échoué (les données précédentes sont gardées) ; `skipped` : éléments illisibles ignorés.
 */
export const AxonautReadState = z.object({
  at: IsoDateTime,
  ok: z.boolean(),
  error: z.string().nullable(),
  skipped: z.int().min(0),
});
export type AxonautReadState = z.infer<typeof AxonautReadState>;

/** `GET /api/suppliers` : tous les fournisseurs (sous-traitants d'abord), pour choisir et pour classer. */
export const SupplierList = z.object({
  items: z.array(Supplier),
  /** Dernière lecture des fournisseurs par la synchro Axonaut (null : jamais lus). */
  syncedAt: IsoDateTime.nullable(),
  /** Dernière tentative de lecture (réussie ou non) ; null : aucune synchro depuis que cet état est gardé. */
  lastRead: AxonautReadState.pick({ at: true, error: true, skipped: true }).nullable(),
});
export type SupplierList = z.infer<typeof SupplierList>;

export const SupplierIdParams = z.object({ id: Uuid });

/** `PUT /api/admin/suppliers/:id/subcontractor` (administrateur, comme les autres réglages de Chiffres). */
export const ClassifySupplierBody = z.object({ classification: SubcontractorClassification });
export type ClassifySupplierBody = z.infer<typeof ClassifySupplierBody>;

/** Un sous-traitant dans la recherche de l'en-tête (Ctrl / ⌘ + K) : « 4 devis · 7 400 € confiés en 2026 ». */
export const SubcontractorDirectoryItem = z.object({
  id: Uuid,
  name: z.string(),
  year: z.int(),
  quotesCount: z.int().min(0),
  confiedCents: Cents,
});
export type SubcontractorDirectoryItem = z.infer<typeof SubcontractorDirectoryItem>;
export const SubcontractorDirectory = z.object({ items: z.array(SubcontractorDirectoryItem) });
export type SubcontractorDirectory = z.infer<typeof SubcontractorDirectory>;

// ------------------------------------------------------------------ Sous-traitance d'un devis

export const QuoteIdParams = z.object({ id: Uuid });

const SubcontractAssignment = z.object({
  supplierId: Uuid,
  supplierName: z.string(),
  costCents: Cents,
});

/** Une ligne du devis (rang et clé stable), et sa sous-traitance éventuelle. */
export const SubcontractQuoteLine = z.object({
  /** `<rang>:<empreinte>` (`quoteLineKey`) : l'API Axonaut ne donne pas d'identifiant de ligne. */
  key: z.string(),
  index: z.int().min(0),
  label: z.string(),
  product: z.string().nullable(),
  category: z.string().nullable(),
  amountCents: Cents,
  /** Ligne de maquette (catégorie de création de site, ou libellé) : coche « Maquette sous-traitée ». */
  mockup: z.boolean(),
  subcontract: SubcontractAssignment.nullable(),
  /** La ligne enregistrée a changé de rang dans Axonaut (retrouvée par son empreinte). */
  moved: z.boolean(),
});
export type SubcontractQuoteLine = z.infer<typeof SubcontractQuoteLine>;

export const QuoteSubcontracting = z.object({
  quoteId: Uuid,
  number: z.string().nullable(),
  title: z.string().nullable(),
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  quoteStatus: z.enum(['pending', 'accepted', 'refused']),
  amountCents: Cents,
  signedOn: IsoDate.nullable(),
  status: SubcontractStatus,
  lines: z.array(SubcontractQuoteLine),
  global: SubcontractAssignment.nullable(),
  /** Lignes enregistrées qui n'existent plus dans le devis Axonaut : leur coût reste compté (au prorata). */
  orphans: z.array(SubcontractAssignment.extend({ label: z.string() })),
  /** Sous-traité plafonné au montant du devis, et part d'Evolusite. */
  subcontractedCents: Cents,
  evolusiteCents: Cents,
  /** Coût total confié (non plafonné) : au-dessus du montant du devis, l'écran prévient. */
  costCents: Cents,
  /** Une ligne de maquette est sous-traitée. */
  mockupSubcontracted: z.boolean(),
  due: z.object({
    /** « À payer au plus tard » (null sans date de départ). */
    date: IsoDate.nullable(),
    start: IsoDate.nullable(),
    startSource: z.enum(['project', 'signature', 'none']),
    delayMonths: z.int().min(0),
    /** Date choisie à la main sur ce devis (« Changer la date »). */
    overridden: z.boolean(),
  }),
  /** `draft` : recopié du brouillon de devis (lot AT) ; `user` : saisi ou modifié par un membre. */
  source: z.enum(['user', 'draft']).nullable(),
  setBy: z.object({ id: Uuid, name: z.string() }).nullable(),
  setAt: IsoDateTime.nullable(),
});
export type QuoteSubcontracting = z.infer<typeof QuoteSubcontracting>;

/** `PUT /api/quotes/:id/subcontracting` : remplace la sous-traitance du devis. */
export const PutQuoteSubcontractingBody = z
  .object({
    mode: SubcontractMode,
    lines: z
      .array(
        z.object({
          key: z.string().min(1).max(40),
          supplierId: Uuid,
          costCents: Cents.min(0).max(100_000_000),
        }),
      )
      .max(200)
      .default([]),
    global: z
      .object({ supplierId: Uuid, costCents: Cents.min(1).max(100_000_000) })
      .nullable()
      .default(null),
  })
  .superRefine((body, ctx) => {
    if (body.mode === 'lines' && body.lines.length === 0)
      ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Au moins une ligne sous-traitée attendue' });
    if (body.mode === 'global' && !body.global)
      ctx.addIssue({ code: 'custom', path: ['global'], message: 'Sous-traitant et montant attendus' });
    const keys = body.lines.map((l) => l.key);
    if (new Set(keys).size !== keys.length)
      ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Une ligne apparaît deux fois' });
  });
export type PutQuoteSubcontractingBody = z.input<typeof PutQuoteSubcontractingBody>;

/** `PUT /api/quotes/:id/subcontracting/due-date` : « Changer la date » (null = revenir au calcul). */
export const PutSubcontractDueDateBody = z.object({ date: IsoDate.nullable() });
export type PutSubcontractDueDateBody = z.infer<typeof PutSubcontractDueDateBody>;

// ------------------------------------------------------------------ Fiche sous-traitant

export const SubcontractAttachment = z.object({
  quoteId: Uuid.nullable(),
  number: z.string().nullable(),
  clientName: z.string().nullable(),
  /** `auto` : rattachée d'office (calcul) ; `user` : choisie à la main. */
  how: z.enum(['auto', 'user']),
});
export type SubcontractAttachment = z.infer<typeof SubcontractAttachment>;

/** Une facture (dépense Axonaut) d'un sous-traitant. */
export const SubcontractInvoice = z.object({
  expenseId: Uuid,
  supplierId: Uuid.nullable(),
  supplierName: z.string().nullable(),
  label: z.string(),
  number: z.string().nullable(),
  date: IsoDate,
  amountCents: Cents,
  /** Reste à payer (HT, au prorata du TTC d'Axonaut) ; 0 = payée. */
  leftToPayCents: Cents,
  paidDate: IsoDate.nullable(),
  expectedPaymentDate: IsoDate.nullable(),
  attachment: SubcontractAttachment,
  /** Document de la dépense dans Axonaut (`public_path` fourni par l'API), null s'il manque. */
  axonautUrl: z.string().nullable(),
});
export type SubcontractInvoice = z.infer<typeof SubcontractInvoice>;

/** Ce qui est confié à un sous-traitant sur un devis (engagement). */
export const SubcontractCommitment = z.object({
  quoteId: Uuid,
  number: z.string().nullable(),
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  supplierId: Uuid,
  supplierName: z.string(),
  /** « Intégration, module de réservation » ou « Montant global ». */
  what: z.string(),
  signedOn: IsoDate.nullable(),
  costCents: Cents,
  invoicedCents: Cents,
  paidCents: Cents,
  remainingCents: Cents,
  dueDate: IsoDate.nullable(),
  /** Date « au plus tard » passée avec un reste à payer. */
  late: z.boolean(),
});
export type SubcontractCommitment = z.infer<typeof SubcontractCommitment>;

/** `GET /api/subcontractors/:id` */
export const SubcontractorPage = z.object({
  supplier: Supplier,
  year: z.int(),
  stats: z.object({
    /** Confié sur les devis signés cette année. */
    confiedCents: Cents,
    invoicedCents: Cents,
    paidCents: Cents,
    remainingCents: Cents,
  }),
  /** Devis par devis : signés cette année, ou plus anciens encore à payer. */
  commitments: z.array(SubcontractCommitment),
  /** Ses factures : dépenses Axonaut de ce fournisseur (cette année, ou rattachées à un devis affiché). */
  invoices: z.array(SubcontractInvoice),
});
export type SubcontractorPage = z.infer<typeof SubcontractorPage>;

// ------------------------------------------------------------------ Dépenses des sous-traitants

/** `GET /api/subcontracting/expenses` : dépenses des sous-traitants et leur devis (carte de l'onglet Dépenses). */
export const SubcontractExpenses = z.object({
  items: z.array(SubcontractInvoice),
  /** Devis ouverts par sous-traitant, pour « Changer » / « Rattacher ». */
  commitments: z.array(SubcontractCommitment),
});
export type SubcontractExpenses = z.infer<typeof SubcontractExpenses>;

export const ExpenseIdParams = z.object({ id: Uuid });

/** `PUT /api/expenses/:id/subcontract` : « Changer » / « Rattacher » (le choix à la main gagne). */
export const AttachSubcontractExpenseBody = z
  .object({ link: z.enum(['quote', 'none', 'auto']), quoteId: Uuid.optional() })
  .refine((b) => b.link !== 'quote' || !!b.quoteId, { message: 'Devis attendu', path: ['quoteId'] });
export type AttachSubcontractExpenseBody = z.infer<typeof AttachSubcontractExpenseBody>;

// ------------------------------------------------------------------ Reprise des devis

export const SubcontractReviewItem = z.object({
  quoteId: Uuid,
  number: z.string().nullable(),
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  signedOn: IsoDate,
  amountCents: Cents,
  status: SubcontractStatus,
  subcontractedCents: Cents,
  /** Sous-traitants du devis (renseigné). */
  suppliers: z.array(z.string()),
  /**
   * « À vérifier » : une dépense 604 / 611 tombe près de la signature (15 jours avant à 90 jours après) sans être
   * rattachée à un autre devis. Une aide : rien n'est rempli tout seul.
   */
  hint: z.string().nullable(),
});
export type SubcontractReviewItem = z.infer<typeof SubcontractReviewItem>;

/** `GET /api/subcontracting/review` : devis signés depuis le 1er janvier, à renseigner d'abord. */
export const SubcontractReview = z.object({
  year: z.int(),
  total: z.int().min(0),
  toReview: z.int().min(0),
  items: z.array(SubcontractReviewItem),
});
export type SubcontractReview = z.infer<typeof SubcontractReview>;

/** `settings.subcontracting` (Réglages > Chiffres). */
export const SubcontractingSettings = z.object({
  /** Délai de paiement des sous-traitants après le début du projet, en mois. */
  paymentDelayMonths: z.int().min(0).max(24),
});
export type SubcontractingSettings = z.infer<typeof SubcontractingSettings>;
